// Inline teach-interrupt modal. Triggered server-side via TEACH_PROMPT message on critical errors.
// In FAMILIARISATION (lenient) mode it pauses the scenario and forces a quick-check answer.
// In all other modes it auto-dismisses to the teach feed.

import { useEffect, useState } from 'react';

export interface SocraticPrompt {
  trigger_key: string;
  lead_md: string;
  why_it_matters_md: string;
  quick_check: Array<{ q: string; options: string[]; correct_idx: number; explain?: string }>;
  guideline_ref?: string;
  difficulty: string;
  t_ms: number;
}

interface Props {
  prompt: SocraticPrompt | null;
  onDismiss: () => void;
  /** Called once the learner has answered all questions. */
  onComplete: () => void;
}

export default function SocraticPromptModal({ prompt, onDismiss, onComplete }: Props) {
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [revealed, setRevealed] = useState<Record<number, boolean>>({});

  useEffect(() => {
    setAnswers({});
    setRevealed({});
  }, [prompt?.t_ms]);

  if (!prompt) return null;

  // Only block in familiarisation. Otherwise show a non-blocking toast that auto-dismisses.
  const isLenient = prompt.difficulty === 'familiarisation';
  if (!isLenient) {
    // Auto-dismiss; the teach feed will hold a copy.
    setTimeout(onDismiss, 0);
    return null;
  }

  const allAnswered = prompt.quick_check.every((_, i) => answers[i] !== undefined);
  const allRevealed = prompt.quick_check.every((_, i) => revealed[i]);

  return (
    <div className="fixed inset-0 z-50 bg-ink-950/90 backdrop-blur-sm overflow-y-auto py-6 px-4 flex items-start justify-center">
      <div className="panel max-w-2xl w-full p-6 border-l-4 border-alarm-amber my-auto">
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-alarm-amber">Hold up — let's look at this together</p>
            <p className="text-mono-white/50 text-xs mt-1">T+{(prompt.t_ms / 1000).toFixed(0)}s · {prompt.guideline_ref || 'RCUK Adult ALS'}</p>
          </div>
        </div>

        <div className="prose prose-invert max-w-none text-sm leading-relaxed">
          <h3 className="text-mono-white">What just happened</h3>
          <p className="text-mono-white/85">{prompt.lead_md}</p>

          <h3 className="text-mono-white">Why it matters</h3>
          <p className="text-mono-white/85">{prompt.why_it_matters_md}</p>

          <h3 className="text-mono-white">Quick check</h3>
          <div className="space-y-4 not-prose">
            {prompt.quick_check.map((q, i) => {
              const chosen = answers[i];
              const correct = q.correct_idx;
              const showFeedback = chosen !== undefined && revealed[i];
              return (
                <div key={i}>
                  <p className="text-mono-white/90 mb-2">{i + 1}. {q.q}</p>
                  <div className="space-y-1">
                    {q.options.map((opt, j) => {
                      const isChosen = chosen === j;
                      const isCorrect = j === correct;
                      const cls = !showFeedback
                        ? isChosen ? 'bg-brand-600 text-white' : 'bg-ink-800 hover:bg-ink-900 text-mono-white/85'
                        : isCorrect ? 'bg-mono-green/20 text-mono-green ring-1 ring-mono-green/50'
                          : isChosen ? 'bg-alarm-red/15 text-alarm-red ring-1 ring-alarm-red/40'
                          : 'bg-ink-800 text-mono-white/50';
                      return (
                        <button
                          key={j}
                          onClick={() => {
                            if (revealed[i]) return;
                            setAnswers((a) => ({ ...a, [i]: j }));
                            setRevealed((r) => ({ ...r, [i]: true }));
                          }}
                          className={`w-full text-left px-3 py-2 rounded text-sm ${cls}`}
                        >
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                  {showFeedback && q.explain && (
                    <p className="mt-2 text-xs text-mono-white/60 italic">{q.explain}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-6 flex justify-between items-center">
          <button onClick={onDismiss} className="btn-ghost text-sm">Skip and continue</button>
          <button
            onClick={() => { onComplete(); }}
            disabled={!allRevealed}
            className="btn-primary text-sm disabled:opacity-50"
          >
            {allRevealed ? 'Got it — back to scenario' : `${Object.keys(revealed).length}/${prompt.quick_check.length} answered`}
          </button>
        </div>
      </div>
    </div>
  );
}
